export interface IAskKodiakProps {
  richtext?: TrustedHTML | string | null;
}

export interface AskKodiakResponse {
  NaicsEdition: string;
  NbHits: number;
  Page: number;
  NbPages: number;
  HitsPerPage: number;
  Query: string;
  ProcessingTimeMS: number;
  ExhaustiveNbHits: boolean;
  Hits: AskKodiakHit[];
}

export interface AskKodiakHit {
  Code: string;
  Hash: string;
  Description: string;
  Path: string[];
  Sic: string;
  SicDetail: AskKodiakSicDetail[];
  SectorCode: string;
  SectorTitle: string;
  SubsectorCode: string;
  SubsectorTitle: string;
  IndustryGroupCode: string;
  IndustryGroupTitle: string;
  InternationalIndustryCode: string;
  InternationalIndustryTitle: string;
  NationalIndustryCode: string;
  NationalIndustryTitle: string;
  //optional: The long-form description of the NAICS national industry group, if requested by the includeNationalIndustryDescription param
  NationalIndustryDescription: string;
  ReplacedBy: string[];
  Replaces: string[];
  EconomicCensus2012Payroll: number;
  EconomicCensus2012NumEstablishments: number;
  EconomicCensus2012Employees: number;
  EconomicCensus2012Receipts: number;
  ObjectID: string;
}

export interface AskKodiakSicDetail {
  Code: string;
  Title: string;
}

// Simplified interfaces focused on what we actually use
export interface AskKodiakProductAppetiteResponse {
  CompanyCount: number;
  ProductCount: number;
  EligibleProductCount: number;
  EligibleGroupedProductCount: number;
  EligibleNotGroupedProductCount: number;
  IncludeIneligible: boolean;
  ProductGroupingsSource: string;
  VirtualCompaniesSource: string;
  CompaniesPerPage: number;
  Page: number;
  Pages: number;
  NaicsEdition: string;
  Filters: AskKodiakFilters;
  Companies: AskKodiakCompany[];
}

export interface AskKodiakFilters {
  NaicsCodes: string[];
  OriginalNaicsCodes: string[];
  OriginalNaicsCodesEdition: string;
}

export interface AskKodiakCompany {
  NumEligible: number;
  NumEligibleGrouped: number;
  NumEligibleNotGrouped: number;
  NumProducts: number;
  Score: number;
  AmBest: AskKodiakAmBest;
  Appointments: AskKodiakAppointments;
  Description: string;
  Id: string;
  IsCarrier: boolean;
  Joined?: number;
  LastUpdated: AskKodiakLastUpdated;
  Location: string;
  Logo: string;
  Naic: string;
  Name: string;
  Phone: string;
  ProductGroupings: AskKodiakProductGrouping[];
  Shortname: string;
  Stealth?: boolean;
  StorefrontURL: string;
  Theme: AskKodiakTheme;
  Website: string;
}

export interface AskKodiakAmBest {
  Rating: string;
}

export interface AskKodiakAppointments {
  Description: string;
  Link: string;
}

export interface AskKodiakLastUpdated {
  Storefront: AskKodiakStorefrontUpdate;
}

export interface AskKodiakStorefrontUpdate {
  By: string;
  On: number;
}

export interface AskKodiakTheme {
  Color: string;
  FontColor: string;
  Text: AskKodiakThemeText;
}

export interface AskKodiakThemeText {
  Color: string;
  Contrast?: number;
}

export interface AskKodiakProductGrouping {
  NumEligible: number;
  NumProducts: number;
  Score: number;
  GoverningCode: string;
  Name: string;
  ProductCodes: string[];
  Products: AskKodiakProduct[];
}

export interface AskKodiakProduct {
  Eligible: boolean;
  Score: number;
  Actions: AskKodiakActions;
  Classes: { [key: string]: string[] };
  CoverageType: string[];
  Id: string;
  Logo: string;
  Name: string;
}

export interface AskKodiakActions {
  Contact: string;
  Url: string;
}

export interface AskKodiakProductAppetite {
  id: ProductCode;
  name: string;
  isThereAppetite: boolean;
}

// Type-safe product codes
export type ProductCode = 'AUTOB' | 'PROPC' | 'GL' | 'WORK' | 'UMBRC';

export const PRODUCT_CODES: Record<ProductCode, string> = {
  AUTOB: 'Auto',
  PROPC: 'Property',
  GL: 'General liability',
  WORK: 'Workers comp',
  UMBRC: 'Umbrella',
} as const;

export const LIBERTY_MUTUAL_COMPANY_NAME = 'Liberty Mutual';

export const askKodiakAppetiteCodes: ProductCode[] = ['AUTOB', 'PROPC', 'GL', 'WORK', 'UMBRC'];

// Factory function to create fresh appetite list
export const createProductAppetiteList = (): AskKodiakProductAppetite[] => [
  {
    id: 'AUTOB',
    name: PRODUCT_CODES.AUTOB,
    isThereAppetite: false,
  },
  {
    id: 'UMBRC',
    name: PRODUCT_CODES.UMBRC,
    isThereAppetite: false,
  },
  {
    id: 'GL',
    name: PRODUCT_CODES.GL,
    isThereAppetite: false,
  },
  {
    id: 'WORK',
    name: PRODUCT_CODES.WORK,
    isThereAppetite: false,
  },
  {
    id: 'PROPC',
    name: PRODUCT_CODES.PROPC,
    isThereAppetite: false,
  },
];

// Legacy export for backward compatibility - now dynamically created
export const askKodiakProductAppetiteList: AskKodiakProductAppetite[] = createProductAppetiteList();

export const FARM_PRODUCT_NAMES: Record<ProductCode, string> = {
  AUTOB: 'Farm Auto',
  PROPC: 'Farm Property',
  GL: 'Farm General Liability',
  WORK: 'Workers Comp',
  UMBRC: 'Farm Umbrella',
} as const;

export const isFarmSearchHit = (hit: AskKodiakHit): boolean => {
  return hit.Code?.startsWith('11') ?? false;
};

export const normalizeAskKodiakProductCodeForFarmMode = (code: string): ProductCode | null => {
  const farmCodeMap: Record<string, ProductCode> = {
    AUTOF: 'AUTOB',
    PROPF: 'PROPC',
    CGLF: 'GL',
    EXLIAF: 'UMBRC',
    WORKF: 'WORK',
  };
  return farmCodeMap[code] ?? null;
};
